import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import AssignmentLateOutlinedIcon from '@mui/icons-material/AssignmentLateOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined'
import HourglassTopOutlinedIcon from '@mui/icons-material/HourglassTopOutlined'
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined'
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined'
import FilterAltOffOutlinedIcon from '@mui/icons-material/FilterAltOffOutlined'
import DownloadSharpIcon from '@mui/icons-material/DownloadSharp'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import {
  FormProvider,
  RHFDateRangePickerField,
  RHFSelectField,
  RHFTextField,
} from '../../common/hook-form'
import ProzoDataGrid from '../../common/ProzoDataGrid'
import PageTitle from '../../common/PageTitle'
import Loader from '../../common/Loader'
import withLayout from '../../hocs/Layout'
import { apiConstants, postGeneric } from '../../common/constants'
import { formatCurrency, formatDate } from '../../helpers/UtilityHelper'
import { getMerchant } from '../../helpers/ReduxHelpers'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { FONT, SIZE, WEIGHT } from '../../styles/foundation/typography'
import { NAVY, BORDER, SUBTLE, WHITE, PRIMARY_BLUE, SUCCESS, ERROR, WARNING } from '../../styles/foundation/colors'
import { SHADOW } from '../../styles/foundation/effects'
import { pageContainerSx, helpTextSx, helpTextTypeSx, primaryBtnSx, secondaryBtnSx } from '../../styles/patterns'

// ── Field normalisers ──────────────────────────────────────────────────────────
const rowId = (row) => row.claimId || row.claim_id || row.id || row._id || ''
const rowStatus = (row) => row.claimStatus || row.status || ''
const rowAwb = (row) =>
  row.awb || row.awbNumber || row.proshipOnlyData?.proshipAwbNumber || row.orderData?.orderAwb || ''
const rowFiledAt = (row) => row.filedAt || row.claimRegisteredDate || ''

// ── Lookups ────────────────────────────────────────────────────────────────────
const CLAIM_TYPE_LABELS = {
  DNR: 'Delivered Not Received',
  DAMAGED: 'Damaged',
  WRONG_ITEM: 'Wrong Item',
  PARTIAL: 'Partial Delivery',
  EMPTY: 'Empty Parcel',
  NEVER_PICKED: 'Never Picked',
  LOST: 'Lost Shipment',
  HANDOVER_DISPUTE: 'Handover Dispute',
}

const STATUS_OPTIONS = [
  { label: 'All', value: '' },
  { label: 'Pending', value: 'PENDING' },
  { label: 'Under Review', value: 'UNDER_REVIEW' },
  { label: 'Documents Pending', value: 'DOCUMENTS_PENDING' },
  { label: 'More Data Requested', value: 'MORE_DATA_REQUESTED' },
  { label: 'Data Uploaded', value: 'DATA_UPLOADED' },
  { label: 'Approved', value: 'APPROVED' },
  { label: 'Partially Approved', value: 'PARTIALLY_APPROVED' },
  { label: 'Rejected', value: 'REJECTED' },
  { label: 'Closed', value: 'CLOSED' },
]

const CLAIM_TYPE_OPTIONS = [
  { label: 'All', value: '' },
  { label: 'Delivered Not Received', value: 'DNR' },
  { label: 'Damaged', value: 'DAMAGED' },
  { label: 'Wrong Item', value: 'WRONG_ITEM' },
  { label: 'Partial Delivery', value: 'PARTIAL' },
  { label: 'Empty Parcel', value: 'EMPTY' },
  { label: 'Never Picked', value: 'NEVER_PICKED' },
  { label: 'Lost Shipment', value: 'LOST' },
  { label: 'Handover Dispute', value: 'HANDOVER_DISPUTE' },
]

const SHIPMENT_TYPE_OPTIONS = [
  { label: 'All', value: '' },
  { label: 'Forward', value: 'FORWARD' },
  { label: 'RVP', value: 'RVP' },
  { label: 'RTO', value: 'RTO' },
  { label: 'Handover', value: 'HANDOVER' },
]

const statusColor = (s) => {
  switch (s) {
    case 'PENDING':
    case 'DOCUMENTS_PENDING':
    case 'MORE_DATA_REQUESTED':
      return 'warning'
    case 'UNDER_REVIEW':
    case 'DATA_UPLOADED':
      return 'info'
    case 'APPROVED':
    case 'PARTIALLY_APPROVED':
      return 'success'
    case 'CLOSED':
      return 'default'
    case 'REJECTED':
      return 'error'
    default:
      return 'primary'
  }
}

const prettyStatus = (s) =>
  s ? s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : '—'

const prettyClaimType = (t) => CLAIM_TYPE_LABELS[t] || (t ? t.replace(/_/g, ' ') : '—')

// ── Design primitives (HomePage-style) ──────────────────────────────────────
const statCardSx = {
  bgcolor: WHITE, borderRadius: '10px', p: '16px', textAlign: 'center',
  boxShadow: SHADOW.card, border: `1px solid ${BORDER}`,
  transition: 'transform 0.2s, box-shadow 0.2s',
  '&:hover': { transform: 'translateY(-2px)', boxShadow: SHADOW.cardHover },
}
const sectionCardSx = {
  bgcolor: WHITE, borderRadius: '10px', boxShadow: SHADOW.card,
  border: `1px solid ${BORDER}`, overflow: 'hidden', mb: 2.5,
}

// ── Component ─────────────────────────────────────────────────────────────────
function AllClaims() {
  const { enqueueSnackbar } = useSnackbar()
  const merchant = getMerchant()
  const merchantId = merchant?.id || ''

  const methods = useForm({
    defaultValues: {
      awb: '',
      claimId: '',
      status: '',
      claimType: '',
      shipmentType: '',
      fromDate: '',
      toDate: '',
    },
  })
  const { handleSubmit, reset } = methods

  const [rows, setRows] = useState([])
  const [page, setPage] = useState(0)
  const [sizePerPage, setSizePerPage] = useState(20)
  const [filters, setFilters] = useState({})
  const [loading, setLoading] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)

  useEffect(() => {
    if (!merchantId || !hasSearched) return
    setLoading(true)
    const body = {
      merchantId,
      ...filters,
      skip: page * sizePerPage,
      limit: sizePerPage,
    }
    postGeneric(apiConstants.CLAIMS_LIST, body)
      .then((res) => {
        const data =
          res?.data?.responseObj || res?.data?.claims || res?.data?.data || res?.data || []
        setRows(
          (Array.isArray(data) ? data : []).map((r, i) => ({
            ...r,
            _gridId: rowId(r) || `row-${i}`,
          }))
        )
      })
      .catch((err) => {
        console.error(err)
        enqueueSnackbar('Failed to fetch claims', { variant: 'error' })
      })
      .finally(() => setLoading(false))
  }, [filters, page, sizePerPage, merchantId, hasSearched, enqueueSnackbar])

  const onSubmit = (values) => {
    const f = {}
    if (values.awb) f.awbNumber = values.awb
    if (values.claimId) f.claimId = values.claimId
    if (values.status) f.status = [values.status]
    if (values.claimType) f.claimType = values.claimType
    if (values.shipmentType) f.shipmentType = values.shipmentType
    if (values.fromDate) f.fromDate = formatDate(values.fromDate, 'yyyy-MM-dd')
    if (values.toDate) f.toDate = formatDate(values.toDate, 'yyyy-MM-dd')
    setFilters(f)
    setPage(0)
    setHasSearched(true)
  }

  const onReset = () => {
    reset({
      awb: '',
      claimId: '',
      status: '',
      claimType: '',
      shipmentType: '',
      fromDate: '',
      toDate: '',
    })
    setFilters({})
    setRows([])
    setPage(0)
    setHasSearched(false)
  }

  const exportCsv = () => {
    if (!rows.length) {
      enqueueSnackbar('No data to export', { variant: 'info' })
      return
    }
    const headers = [
      'Claim ID', 'AWB', 'Order ID', 'Courier', 'Claim Type',
      'Shipment Type', 'Invoice Value', 'Filed Date', 'Status', 'Aging (days)',
    ]
    const lines = [headers.join(',')]
    rows.forEach((r) => {
      const filed = rowFiledAt(r)
      const aging = filed ? Math.floor((Date.now() - new Date(filed).getTime()) / 86400000) : ''
      const cells = [
        rowId(r),
        rowAwb(r),
        r.orderId || r.orderData?.orderId,
        r.courierCode || r.courierPartner || r.courierId,
        r.claimType,
        r.shipmentType,
        r.invoiceValue,
        filed ? formatDate(filed, 'yyyy-MM-dd HH:mm') : '',
        rowStatus(r),
        aging,
      ]
        .map((v) => (v == null ? '' : String(v).replace(/"/g, '""')))
        .map((v) => (/[,"\n]/.test(v) ? `"${v}"` : v))
      lines.push(cells.join(','))
    })
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `claims_${formatDate(new Date(), 'yyyyMMdd_HHmm')}.csv`
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  }

  const kpis = useMemo(() => {
    const total = rows.length
    const open = rows.filter((r) =>
      ['PENDING', 'UNDER_REVIEW', 'DOCUMENTS_PENDING', 'MORE_DATA_REQUESTED', 'DATA_UPLOADED'].includes(rowStatus(r))
    ).length
    const approved = rows.filter((r) =>
      ['APPROVED', 'PARTIALLY_APPROVED'].includes(rowStatus(r))
    ).length
    const rejected = rows.filter((r) => rowStatus(r) === 'REJECTED').length
    return { total, open, approved, rejected }
  }, [rows])

  const columns = [
    {
      field: 'claimId',
      headerName: 'Claim ID',
      width: 230,
      renderCell: ({ row }) => {
        const id = rowId(row)
        return id ? (
          <Link
            to={`/claims/${id}`}
            style={{ color: '#1565c0', textDecoration: 'none', fontWeight: 600, fontFamily: 'monospace' }}
          >
            {id}
          </Link>
        ) : '—'
      },
    },
    {
      field: 'awb',
      headerName: 'AWB',
      width: 160,
      renderCell: ({ row }) => {
        const awb = rowAwb(row)
        return awb ? (
          <Link
            to={`/order-detail?waybill=${awb}`}
            style={{ color: '#1565c0', textDecoration: 'none', fontFamily: 'monospace' }}
          >
            {awb}
          </Link>
        ) : '—'
      },
    },
    {
      field: 'orderId',
      headerName: 'Order ID',
      width: 150,
      renderCell: ({ row }) => row.orderId || row.orderData?.orderId || '—',
    },
    {
      field: 'claimType',
      headerName: 'Claim Type',
      width: 200,
      renderCell: ({ row }) =>
        row.claimType ? (
          <Chip
            size="small"
            variant="outlined"
            label={prettyClaimType(row.claimType)}
            sx={{ height: 22 }}
          />
        ) : '—',
    },
    {
      field: 'shipmentType',
      headerName: 'Shipment',
      width: 110,
      renderCell: ({ row }) => row.shipmentType || '—',
    },
    {
      field: 'courier',
      headerName: 'Courier',
      width: 150,
      renderCell: ({ row }) => row.courierCode || row.courierPartner || row.courierId || '—',
    },
    {
      field: 'invoiceValue',
      headerName: 'Invoice Value',
      width: 130,
      align: 'right',
      headerAlign: 'right',
      renderCell: ({ row }) =>
        row.invoiceValue != null ? (
          <Typography variant="body2" fontWeight={600}>{formatCurrency(row.invoiceValue)}</Typography>
        ) : '—',
    },
    {
      field: 'filedAt',
      headerName: 'Filed Date',
      width: 160,
      renderCell: ({ row }) => {
        const d = rowFiledAt(row)
        return d ? formatDate(d, 'dd MMM yyyy HH:mm') : '—'
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 180,
      renderCell: ({ row }) => {
        const s = rowStatus(row)
        return (
          <Chip
            size="small"
            color={statusColor(s)}
            label={prettyStatus(s)}
            sx={{ fontWeight: 500, height: 22 }}
          />
        )
      },
    },
    {
      field: 'aging',
      headerName: 'Aging',
      width: 100,
      renderCell: ({ row }) => {
        const d = rowFiledAt(row)
        if (!d) return '—'
        const hours = (Date.now() - new Date(d).getTime()) / 36e5
        const days = Math.floor(hours / 24)
        const isOpen = ['PENDING', 'UNDER_REVIEW', 'DOCUMENTS_PENDING'].includes(rowStatus(row))
        const breach = isOpen && hours > 72
        return (
          <Chip
            size="small"
            label={`${days}d`}
            color={breach ? 'error' : 'default'}
            variant={breach ? 'filled' : 'outlined'}
            sx={{ height: 22 }}
          />
        )
      },
    },
    {
      field: 'action',
      headerName: 'Action',
      width: 90,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: ({ row }) => {
        const id = rowId(row)
        return (
          <Tooltip title="View claim">
            <span>
              <IconButton
                component={Link}
                to={id ? `/claims/${id}` : '#'}
                disabled={!id}
                size="small"
                color="primary"
              >
                <VisibilityOutlinedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )
      },
    },
  ]

  return (
    <Box sx={pageContainerSx}>
      {loading && <Loader />}
      <PageTitle subTitle="Track and manage insurance claims for lost and damaged shipments">Claims</PageTitle>

      {/* KPI cards */}
      <Box sx={{ display: 'flex', gap: '12px', mb: '20px', flexWrap: 'wrap' }}>
        {[
          { label: 'Total Claims', value: kpis.total, icon: <AssignmentLateOutlinedIcon />, color: PRIMARY_BLUE },
          { label: 'Open / Under Review', value: kpis.open, icon: <HourglassTopOutlinedIcon />, color: WARNING },
          { label: 'Approved', value: kpis.approved, icon: <CheckCircleOutlineIcon />, color: SUCCESS },
          { label: 'Rejected', value: kpis.rejected, icon: <CancelOutlinedIcon />, color: ERROR },
        ].map((s) => (
          <Box key={s.label} sx={{ ...statCardSx, flex: '1 1 160px', minWidth: 140 }}>
            <Box sx={{ color: s.color, display: 'flex', justifyContent: 'center', mb: 0.5, '& svg': { fontSize: 22 } }}>{s.icon}</Box>
            <Typography sx={{ fontSize: '22px', fontWeight: WEIGHT.extra, lineHeight: 1.2, color: s.color }}>{s.value}</Typography>
            <Typography sx={{ color: SUBTLE, fontSize: SIZE['2xs'], mt: '2px', fontWeight: WEIGHT.medium, fontFamily: FONT }}>{s.label}</Typography>
          </Box>
        ))}
      </Box>

      <Box sx={helpTextSx}>
        <InfoOutlinedIcon sx={{ fontSize: 16, color: PRIMARY_BLUE, mt: 0.2 }} />
        <Typography sx={helpTextTypeSx}>
          <strong>How claims work:</strong> Raise claims for delivered, RTO, RVP, or handover disputes.
          Apply filters to search, then track status from Pending → Under Review → Approved/Rejected.
          Export to CSV for offline analysis.
        </Typography>
      </Box>

      {/* Filters */}
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <Box sx={{ ...sectionCardSx, p: 2.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography sx={{ fontFamily: FONT, fontSize: SIZE.sm, fontWeight: WEIGHT.bold, color: NAVY }}>Filters</Typography>
            <Button type="button" startIcon={<DownloadSharpIcon />} variant="outlined" size="small"
              onClick={exportCsv} disabled={!rows.length} sx={secondaryBtnSx}>Export CSV</Button>
          </Box>
          <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6} md={3}>
                  <RHFTextField name="claimId" label="Claim ID" />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <RHFTextField name="awb" label="AWB Number" />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <RHFSelectField name="status" label="Status" options={STATUS_OPTIONS} width="100%" />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <RHFSelectField name="claimType" label="Claim Type" options={CLAIM_TYPE_OPTIONS} width="100%" />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <RHFSelectField name="shipmentType" label="Shipment Type" options={SHIPMENT_TYPE_OPTIONS} width="100%" />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <RHFDateRangePickerField name="filed_date" label="Filed Date" maxDate={new Date()} width="100%" />
                </Grid>
                <Grid item xs={12} md={5} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Button type="submit" variant="contained" size="small" sx={primaryBtnSx}>Apply Filters</Button>
                  <Button type="button" variant="outlined" size="small" sx={secondaryBtnSx}
                    startIcon={<FilterAltOffOutlinedIcon />} onClick={onReset}>Reset</Button>
                </Grid>
              </Grid>
        </Box>
      </FormProvider>

      {/* Claims list */}
      <Box sx={sectionCardSx}>
        <Box sx={{ px: 2.5, py: 2, borderBottom: `1px solid ${BORDER}` }}>
          <Typography sx={{ fontFamily: FONT, fontSize: SIZE.sm, fontWeight: WEIGHT.bold, color: NAVY }}>
            Claims List {hasSearched ? `(${rows.length})` : ''}
          </Typography>
        </Box>
        <Box sx={{ p: 1 }}>
          {!hasSearched ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Box sx={{ width: 48, height: 48, borderRadius: '10px', bgcolor: PRIMARY_BLUE + '12', color: PRIMARY_BLUE, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', mb: 1.5 }}>
                <FilterAltOutlinedIcon />
              </Box>
              <Typography sx={{ fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE.sm, color: NAVY }}>No filters applied yet</Typography>
              <Typography sx={{ fontFamily: FONT, color: SUBTLE, fontSize: SIZE['2xs'], mt: 0.5 }}>
                Choose a status or date range above and click <strong>Apply Filters</strong> to load claims.
              </Typography>
            </Box>
          ) : (
            <ProzoDataGrid columns={columns} autoHeight rows={rows}
              sizePerPage={sizePerPage} setSizePerPage={setSizePerPage}
              setPage={setPage} page={page} pagination hideFooterPagination={false}
              filterMode="server" rowHeight={55} rowCount={Number.MAX_VALUE}
              getRowId={(row) => row._gridId}
            />
          )}
        </Box>
      </Box>
    </Box>
  )
}

export default withLayout(AllClaims)
